import { useRef, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck,
  Copy,
  Check,
  Download,
  RefreshCcw,
  XCircle,
  TerminalSquare,
  GitCompare,
  Radar,
  AlertTriangle,
  ServerCog,
  Network,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { getAssessmentKit, cancelJob, pollJob } from "../lib/api";

const CLASS_LEGEND = [
  { label: "VULNERABLE", cls: "text-red-700 border-red-200 bg-red-50", hint: "version/config matches a vulnerable range" },
  { label: "PATCHED", cls: "text-emerald-700 border-emerald-200 bg-emerald-50", hint: "at or above the fixed version" },
  { label: "NEEDS_VALIDATION", cls: "text-amber-700 border-amber-200 bg-amber-50", hint: "could not be proven either way" },
];

export default function AssessmentKit({ cveId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jobInfo, setJobInfo] = useState(null);
  const [copied, setCopied] = useState(null);
  const cancelledRef = useRef(false);

  const run = async (force = false) => {
    setLoading(true);
    setJobInfo(null);
    cancelledRef.current = false;
    try {
      const { status, data: res } = await getAssessmentKit(cveId, force);
      if (status === 200 && res.assessment_kit) {
        setData(res.assessment_kit);
        toast.success(force ? "Assessment kit regenerated" : "Assessment kit ready", {
          description: res.cached ? "Loaded from cache" : "Freshly generated",
        });
        return;
      }
      if (status === 202 && res.job_id) {
        setJobInfo({ jobId: res.job_id });
        if (res.deduped) {
          toast.info("Joined existing job", { description: "Another request is already running." });
        }
        const job = await pollJob(res.job_id, { intervalMs: 2500, timeoutMs: 180000 });
        if (cancelledRef.current || job.status === "cancelled") {
          toast.info("Generation cancelled");
          return;
        }
        if (job.status === "done" && job.result?.assessment_kit) {
          setData(job.result.assessment_kit);
          setJobInfo(null);
          toast.success("Assessment kit ready", { description: "Freshly generated" });
        } else if (job.status === "failed") {
          throw new Error(job.error || "Job failed");
        } else {
          throw new Error(`Unexpected job state: ${job.status}`);
        }
        return;
      }
      throw new Error("Unexpected response from server");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Generation failed";
      if (err?.response?.status !== 429 && !cancelledRef.current) {
        toast.error("Couldn't build assessment kit", { description: String(detail) });
      }
      setJobInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    if (!jobInfo?.jobId) return;
    cancelledRef.current = true;
    try {
      await cancelJob(jobInfo.jobId);
    } catch (err) {
      toast.error("Couldn't cancel job", { description: String(err?.response?.data?.detail || err?.message) });
    }
  };

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Copied");
      setTimeout(() => setCopied(null), 1400);
    } catch {
      toast.error("Clipboard blocked by browser");
    }
  };

  const download = (filename, text) => {
    const blob = new Blob([text], { type: "text/x-python" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Script downloaded");
  };

  if (!data && !loading) {
    return (
      <section data-testid="assessment-kit" className="panel p-6 md:p-7 fade-up delay-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="overline flex items-center gap-2">
              <ShieldCheck className="h-3 w-3 text-violet-700" />
              Assessment kit · non-destructive
            </div>
            <h3 className="font-display text-xl text-slate-900 mt-1">
              Is this host actually exposed?
            </h3>
            <p className="text-slate-600 text-sm mt-2 max-w-2xl leading-relaxed">
              A pentester-grade kit: root-cause pseudo-code (vulnerable vs fixed logic), a
              Python 3 check script that collects versions/config (and, for network CVEs,
              probes a <code className="text-xs">--target</code> IP) to classify the
              host as <span className="text-red-700">VULNERABLE</span> /{" "}
              <span className="text-emerald-700">PATCHED</span> /{" "}
              <span className="text-amber-700">NEEDS_VALIDATION</span>, plus a Nuclei
              detection template. Non-destructive: it never fires the exploit itself.
            </p>
          </div>
          <button
            onClick={() => run(false)}
            data-testid="generate-assessment-kit-btn"
            className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors"
          >
            Generate assessment kit
          </button>
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section data-testid="assessment-kit" className="panel p-6 md:p-7 fade-up delay-4 relative overflow-hidden">
        <div className="scanner-line" />
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="overline">Building assessment kit…</div>
          {jobInfo?.jobId && (
            <div className="flex items-center gap-2">
              <span data-testid="assessment-job-badge" className="font-mono text-[10px] text-violet-700 border border-violet-200 bg-violet-50 px-2 py-0.5 rounded-sm">
                JOB {jobInfo.jobId.slice(0, 10)}…
              </span>
              <button onClick={cancel} data-testid="cancel-assessment-btn" className="flex items-center gap-1.5 border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 rounded-md px-2.5 py-1 text-[11px] font-mono transition-colors">
                <XCircle className="h-3 w-3" />
                Cancel
              </button>
            </div>
          )}
        </div>
        <div className="font-mono text-sm text-violet-700 space-y-1">
          <div>Reading the patch diff for the real root cause…</div>
          <div>Deciding local vs remote assessment…</div>
          <div>Writing a read-only Python 3 collector…</div>
          <div>Pinning authoritative fixed versions…</div>
          <div>Running the safety filter<span className="caret">&nbsp;</span></div>
        </div>
        <p className="text-[10px] font-mono text-slate-500 mt-4">
          Background job. Average wait ~45-70s.
        </p>
      </section>
    );
  }

  const rc = data.root_cause_pseudocode || {};
  const script = data.assessment_script || {};
  const nuclei = data.nuclei_template || {};
  const safety = data.safety || {};
  const modeLocal = (data.mode || "local") === "local";

  return (
    <section data-testid="assessment-kit" className="panel p-6 md:p-7 fade-up delay-4">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="overline flex items-center gap-2">
            <ShieldCheck className="h-3 w-3 text-violet-700" />
            Assessment kit · non-destructive
          </div>
          <h3 className="font-display text-xl text-slate-900 mt-1">Verify exposure safely</h3>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid="assessment-mode" className="flex items-center gap-1.5 text-[11px] font-mono border border-slate-300 rounded-md px-2.5 py-1 text-slate-700">
            {modeLocal ? <ServerCog className="h-3.5 w-3.5 text-violet-700" /> : <Network className="h-3.5 w-3.5 text-violet-700" />}
            {modeLocal ? "LOCAL (on host)" : "REMOTE (network)"}
          </span>
          <button onClick={() => run(true)} data-testid="regenerate-assessment-btn" className="flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-1.5 text-xs text-slate-700 hover:border-violet-300 hover:text-violet-700 transition-colors">
            <RefreshCcw className="h-3 w-3" />
            Regenerate
          </button>
        </div>
      </div>

      <SafetyBar safety={safety} fixedVersions={data.fixed_versions} authWarn={script.authoritative_versions_only === false} />

      {data.mode_reason && (
        <p className="text-xs text-slate-600 mb-4 leading-relaxed">
          <span className="overline mr-2">Why this mode</span>
          {data.mode_reason}
        </p>
      )}

      <div className="flex flex-wrap gap-2 mb-4" data-testid="classification-legend">
        {CLASS_LEGEND.map((c) => (
          <span key={c.label} title={c.hint} className={`text-[10px] font-mono uppercase tracking-wider border rounded-sm px-2 py-0.5 ${c.cls}`}>
            {c.label}
          </span>
        ))}
      </div>

      <Tabs defaultValue="script" className="w-full">
        <TabsList className="bg-slate-50 border border-slate-200 rounded-md p-1 mb-4 flex flex-wrap">
          <TabsTrigger value="rootcause" data-testid="tab-rootcause" className="font-mono text-xs data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm px-3 py-1.5 rounded-sm text-slate-700">
            <GitCompare className="h-3.5 w-3.5 mr-1.5" /> Root cause
          </TabsTrigger>
          <TabsTrigger value="script" data-testid="tab-script" className="font-mono text-xs data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm px-3 py-1.5 rounded-sm text-slate-700">
            <TerminalSquare className="h-3.5 w-3.5 mr-1.5" /> Check script
          </TabsTrigger>
          <TabsTrigger value="nuclei" data-testid="tab-nuclei" disabled={!nuclei.available} className="font-mono text-xs data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm px-3 py-1.5 rounded-sm text-slate-700 disabled:opacity-40">
            <Radar className="h-3.5 w-3.5 mr-1.5" /> Nuclei{!nuclei.available && <span className="ml-1 text-[9px]">N/A</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rootcause" className="mt-0 space-y-3">
          <CodePane title="Vulnerable logic" lang={rc.language || "pseudo"} code={rc.vulnerable} tone="red" copied={copied === "vuln"} onCopy={() => copy("vuln", rc.vulnerable || "")} testid="rootcause-vulnerable" />
          <CodePane title="Fixed logic (from patch)" lang={rc.language || "pseudo"} code={rc.fixed} tone="green" copied={copied === "fixed"} onCopy={() => copy("fixed", rc.fixed || "")} testid="rootcause-fixed" />
          {rc.explanation && (
            <p className="text-sm text-slate-700 leading-relaxed bg-slate-50 border border-slate-200 rounded-md p-4">
              {rc.explanation}
            </p>
          )}
        </TabsContent>

        <TabsContent value="script" className="mt-0">
          {false ? null : (
            <div className="bg-slate-50 border border-slate-200 rounded-md overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-slate-50">
                <span className="text-[10px] font-mono uppercase tracking-wider text-violet-700 border border-violet-200 bg-violet-50 px-1.5 py-0.5 rounded-sm">python3</span>
                <span className="font-mono text-xs text-slate-700 truncate flex-1">{script.filename || "assessment.py"}</span>
                <span data-testid="script-mode" className="text-[10px] font-mono uppercase text-slate-500">{modeLocal ? "local" : "remote --target"}</span>
                <button onClick={() => copy("script", script.code || "")} data-testid="copy-script" className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-violet-700 transition-colors">
                  {copied === "script" ? <><Check className="h-3.5 w-3.5 text-emerald-700" /><span className="text-emerald-700">Copied</span></> : <><Copy className="h-3.5 w-3.5" /><span>Copy</span></>}
                </button>
                <button onClick={() => download(script.filename || "assessment.py", script.code || "")} data-testid="download-script" className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-violet-700 transition-colors">
                  <Download className="h-3.5 w-3.5" /><span>Download</span>
                </button>
              </div>
              <pre className="font-mono text-xs leading-relaxed p-4 text-slate-800 whitespace-pre overflow-x-auto max-h-[480px]">{script.code}</pre>
              {script.usage && (
                <div className="border-t border-slate-100 px-4 py-2.5 bg-slate-50">
                  <span className="overline mr-2">Run</span>
                  <code className="text-xs text-slate-700 break-all">{script.usage}</code>
                  <div className="text-[10px] text-slate-500 mt-1 font-mono">Run with -h / --help for full options.</div>
                </div>
              )}
            </div>
          )}
          {Array.isArray(script.signals_collected) && script.signals_collected.length > 0 && (
            <div className="mt-3 bg-slate-50 border border-slate-200 rounded-md p-4" data-testid="signals-collected">
              <div className="overline mb-2">Signals collected</div>
              <ul className="space-y-1">
                {script.signals_collected.map((s, i) => (
                  <li key={i} className="text-xs text-slate-700 flex gap-2"><span className="text-violet-700 shrink-0">•</span><span>{s}</span></li>
                ))}
              </ul>
              {script.classification_logic && (
                <p className="text-xs text-slate-600 mt-3 leading-relaxed"><span className="overline mr-2">Decision</span>{script.classification_logic}</p>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="nuclei" className="mt-0">
          {!nuclei.available ? (
            <div className="text-slate-500 text-sm p-4 bg-slate-50 border border-slate-200 rounded-md">
              <span className="overline mr-2">Not available</span>
              {nuclei.reason_unavailable || "No reliable version/exposure template for this CVE."}
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-md overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-slate-50">
                <span className="text-[10px] font-mono uppercase tracking-wider text-violet-700 border border-violet-200 bg-violet-50 px-1.5 py-0.5 rounded-sm">yaml</span>
                <span className="font-mono text-xs text-slate-700 truncate flex-1">{nuclei.id || "nuclei template"}</span>
                <button onClick={() => copy("nuclei", nuclei.yaml || "")} data-testid="copy-nuclei" className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-violet-700 transition-colors">
                  {copied === "nuclei" ? <><Check className="h-3.5 w-3.5 text-emerald-700" /><span className="text-emerald-700">Copied</span></> : <><Copy className="h-3.5 w-3.5" /><span>Copy</span></>}
                </button>
              </div>
              <pre className="font-mono text-xs leading-relaxed p-4 text-slate-800 whitespace-pre overflow-x-auto max-h-[480px]">{nuclei.yaml}</pre>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {Array.isArray(data.limitations) && data.limitations.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100" data-testid="assessment-limitations">
          <div className="overline mb-2 flex items-center gap-2"><AlertTriangle className="h-3 w-3 text-amber-700" />Limitations</div>
          <ul className="space-y-1">
            {data.limitations.map((l, i) => (
              <li key={i} className="text-xs text-slate-600 flex gap-2"><span className="text-amber-700 shrink-0">•</span><span>{l}</span></li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function SafetyBar({ safety, fixedVersions, authWarn }) {
  const notes = safety?.violations || [];
  const clean = safety?.read_only && !authWarn;
  return (
    <div
      data-testid="safety-bar"
      data-readonly={clean ? "true" : "false"}
      className={`mb-4 rounded-md p-3 border ${clean ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}
    >
      <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${clean ? "text-emerald-700" : "text-amber-700"}`}>
        {clean ? <ShieldCheck className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
        {clean
          ? "Non-destructive · no review notes"
          : notes.length
          ? `${notes.length} review note(s) — nothing was removed`
          : "Review before running"}
      </div>
      {notes.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-[11px] font-mono text-amber-800">
          {notes.map((v, i) => (
            <li key={i}>{v.artefact}: {v.reason} (review before running)</li>
          ))}
        </ul>
      )}
      {authWarn && (
        <div data-testid="hardcoded-version-warning" className="mt-1.5 text-[11px] font-mono text-amber-700">
          ⚠ Script contains version literals outside the authoritative constant. Verify against the fixed versions below before trusting a PATCHED result.
        </div>
      )}
      <div className="mt-1 text-[10px] font-mono text-slate-500">
        Fixed versions from {safety?.fixed_versions_source || "none"}
        {Array.isArray(fixedVersions) && fixedVersions.filter(Boolean).length > 0 && (
          <span className="text-slate-600"> · {fixedVersions.filter(Boolean).join(", ")}</span>
        )}
      </div>
    </div>
  );
}

function CodePane({ title, lang, code, tone, copied, onCopy, testid }) {
  const dot = tone === "red" ? "bg-red-500" : tone === "green" ? "bg-emerald-500" : "bg-slate-400";
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md overflow-hidden" data-testid={testid}>
      <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-slate-50">
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        <span className="font-mono text-xs text-slate-700 flex-1">{title}</span>
        <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{lang}</span>
        <button onClick={onCopy} className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-violet-700 transition-colors">
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-700" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
      <pre className="font-mono text-xs leading-relaxed p-4 text-slate-800 whitespace-pre overflow-x-auto max-h-[360px]">{code || "-"}</pre>
    </div>
  );
}
